
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { api } from '../../api/support'; 


const MAX_PER_CONV = 100;
const STORAGE_KEY  = 'chat_messages_v3';

function loadPersistedMessages() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
}

function persistMessages(messages) {
  try {
    const toSave = {};
    Object.entries(messages).forEach(([k, v]) => {
      const real = v.filter(m => m.id && !m.optimistic && typeof m.id === 'number');
      if (real.length > 0) toSave[k] = real.slice(-MAX_PER_CONV);
    });
    localStorage.setItem(STORAGE_KEY, JSON.stringify(toSave));
  } catch (e) {
    console.warn('[ChatSlice] persist failed:', e.message);
  }
}

function now() {
  return new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
}

function buildInitials(name) {
  const parts = (name || '').trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return (name || '??').substring(0, 2).toUpperCase();
}

// ═══ INITIAL STATE ═══

const initialState = {
  messages:      { ...loadPersistedMessages() },
  conversations: [],
  activeConvId:  null,
  realConvId:    null,
  typingConvIds: [],
};

// ═══ SLICE ═══

const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    setActiveConvId(state, { payload }) {
  state.activeConvId = payload;
},

    addIncomingMessage(state, { payload: { incoming, myUserId } }) {
      const backendConvId = Number(incoming.conversationId);
      let frontendConvId  = null;

      // Step 1: realConvId match
      if (Number(state.realConvId) === backendConvId)
        frontendConvId = state.activeConvId;

      // Step 2: stored realConvId match
      if (!frontendConvId) {
        const convMatch = state.conversations.find(
          c => Number(c.realConvId) === backendConvId
        );
        if (convMatch) frontendConvId = convMatch.id;
      }

      // Step 3: direct id match
      if (!frontendConvId) {
        const directMatch = state.conversations.find(
          c => Number(c.id) === backendConvId
        );
        if (directMatch) frontendConvId = directMatch.id;
      }

      // Step 4: sender/receiver match
      if (!frontendConvId) {
        const userMatch = state.conversations.find(c =>
          Number(c.masterId) === Number(incoming.senderId)   ||
          Number(c.userId)   === Number(incoming.senderId)   ||
          Number(c.masterId) === Number(incoming.receiverId) ||
          Number(c.userId)   === Number(incoming.receiverId)
        );
        if (userMatch) frontendConvId = userMatch.id;
      }

      // Step 5: fallback
      if (!frontendConvId) frontendConvId = state.activeConvId;
      if (!frontendConvId) frontendConvId = backendConvId;
      if (!frontendConvId) return;

      if (!state.messages[frontendConvId]) state.messages[frontendConvId] = [];

      const isMine = Number(incoming.senderId) === Number(myUserId);

      // ✅ optimistic remove on own send
      if (isMine) {
        state.messages[frontendConvId] = state.messages[frontendConvId]
          .filter(m => !m.optimistic);
      }

  
      if (state.messages[frontendConvId].some(m => m.id === incoming.id)) return;

      state.messages[frontendConvId].push({
        id:       incoming.id,
        sender:   isMine ? 'mine' : 'other',
        senderId: Number(incoming.senderId),
        name:     incoming.name     || 'Unknown',
        initials: incoming.initials || buildInitials(incoming.name || '??'),
        role:     incoming.role     || 'user',
        type:     incoming.type     || 'text',
        text:     incoming.text     || '',
        fileName: incoming.fileName || null,
        fileSize: incoming.fileSize || null,
        mediaUrl: incoming.mediaUrl || null,
        time:     incoming.time     || now(),
        read:     false,
      });

      if (state.messages[frontendConvId].length > MAX_PER_CONV) {
        state.messages[frontendConvId] =
          state.messages[frontendConvId].slice(-MAX_PER_CONV);
      }

      persistMessages(state.messages);

  
      const conv = state.conversations.find(c => c.id === frontendConvId);
      if (conv) {
        conv.preview = incoming.text || (incoming.type === 'file' ? '📎 File' : '');
        conv.time    = 'now';
        if (Number(frontendConvId) !== Number(state.activeConvId) && !isMine)
          conv.unread = (conv.unread || 0) + 1;
      }
    },

    setTypingForConv(state, { payload: { convId, userId, myUserId, isTyping } }) {
      if (Number(userId) === Number(myUserId)) return;
      if (isTyping) {
        if (!state.typingConvIds.includes(convId)) state.typingConvIds.push(convId);
      } else {
        state.typingConvIds = state.typingConvIds.filter(id => id !== convId);
      }
    },

  },
});



export const { addIncomingMessage, setTypingForConv,setActiveConvId  } = chatSlice.actions;

export default chatSlice.reducer;